import { cn } from '@/lib/utils'
import { useTranslation } from '@/hooks'

/**
 * Spinner Component
 * Based on UX Design Guidelines §6.5 + SHARED-LOADING-STATES.md
 *
 * Uses LexAlert logo icon with CSS spin animation
 *
 * Sizes:
 * - sm: 16px
 * - md: 20px (default)
 * - lg: 24px
 * - xl: 32px
 */

export type SpinnerSize = 'sm' | 'md' | 'lg' | 'xl'

export interface SpinnerProps {
  size?: SpinnerSize
  className?: string
  /** Accessible label for screen readers (uses translation if not provided) */
  label?: string
}

const sizeStyles: Record<SpinnerSize, string> = {
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-6 h-6',
  xl: 'w-8 h-8',
}

function LogoIcon({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="-1 -1 74 74" fill="currentColor" className={className} style={style}>
      <g transform="matrix(16.962,0,0,16.962,-6.243,-6.751)">
        <g>
          <g>
            <circle cx="0.92133" cy="3.31163" r="0.02282" transform="translate(-1.02726 0.47989) rotate(-18.98578)"/>
            <circle cx="1.11283" cy="3.7049" r="0.03421" transform="translate(-1.84851 1.2159) rotate(-33.23447)"/>
            <circle cx="1.42663" cy="3.41863" r="0.03422" transform="translate(-1.64244 1.34423) rotate(-33.29376)"/>
            <circle cx="1.89822" cy="3.36552" r="0.04562" transform="translate(-1.53593 1.59449) rotate(-33.29541)"/>
            <circle cx="2.21399" cy="3.5934" r="0.05705" transform="translate(-2.00623 3.5303) rotate(-57.50509)"/>
            <circle cx="2.59546" cy="3.67158" r="0.06844" transform="translate(-1.41199 5.71052) rotate(-81.70965)"/>
            <circle cx="2.96788" cy="3.66068" r="0.07985" transform="translate(-0.89151 0.95624) rotate(-15.94615)"/>
            <circle cx="3.28696" cy="3.35274" r="0.09628" transform="translate(-1.3875 2.91184) rotate(-40.17937)"/>
            <circle cx="1.68883" cy="3.74133" r="0.0456" transform="translate(-2.28286 2.70434) rotate(-51.07274)"/>
            <circle cx="2.03718" cy="3.9683" r="0.057" transform="translate(-2.3983 4.44167) rotate(-68.91749)"/>
            <circle cx="2.4383" cy="4.0778" r="0.06839" transform="translate(-1.77181 6.27978) rotate(-86.73342)"/>
            <circle cx="2.85366" cy="4.0593" r="0.07982" transform="translate(-0.92784 0.84658) rotate(-14.54213)"/>
            <circle cx="3.24345" cy="3.91455" r="0.09122" transform="translate(-1.59153 2.344) rotate(-32.36079)"/>
            <circle cx="3.57024" cy="3.65746" r="0.10266" transform="translate(-1.52519 4.05778) rotate(-50.18356)"/>
            <circle cx="3.80266" cy="3.31273" r="0.11932" transform="translate(-0.6932 5.59782) rotate(-68.00379)"/>
            <circle cx="1.3954" cy="4.03879" r="0.04562" transform="translate(-2.52625 2.34186) rotate(-47.54147)"/>
            <circle cx="1.75164" cy="4.29268" r="0.05702" transform="translate(-2.85931 3.80848) rotate(-61.80728)"/>
            <circle cx="2.15943" cy="4.45084" r="0.06842" transform="translate(-2.67957 5.47903) rotate(-76.11436)"/>
            <circle cx="2.59368" cy="4.50354" r="0.07982" transform="translate(-0.02986 0.01733) rotate(-0.38067)"/>
            <circle cx="3.0275" cy="4.44751" r="0.09123" transform="translate(-1.02677 0.9107) rotate(-14.65583)"/>
            <circle cx="3.43409" cy="4.28624" r="0.10259" transform="translate(-1.64538 2.19728) rotate(-28.94349)"/>
            <circle cx="3.78833" cy="4.02962" r="0.11403" transform="translate(-1.7319 3.68853) rotate(-43.23205)"/>
            <circle cx="4.06836" cy="3.69357" r="0.12544" transform="translate(-1.23295 5.13932) rotate(-57.49012)"/>
            <circle cx="4.25685" cy="3.29887" r="0.14752" transform="translate(-0.2081 6.31011) rotate(-71.7705)"/>
          </g>
          <g>
            <circle cx="4.31873" cy="3.21168" r="0.03421" transform="translate(-0.64884 5.28281) rotate(-59.26954)"/>
            <circle cx="3.92383" cy="3.05521" r="0.03424" transform="translate(-0.70581 4.87103) rotate(-59.32133)"/>
            <circle cx="3.66927" cy="2.65464" r="0.0456" transform="translate(-0.4885 4.45009) rotate(-59.24109)"/>
            <circle cx="3.73557" cy="2.2709" r="0.05702" transform="translate(1.05613 5.72518) rotate(-83.4957)"/>
            <circle cx="3.63854" cy="1.89378" r="0.06842" transform="matrix(0.95249,-0.30457,0.30457,0.95249,-0.40392,1.19816)"/>
            <circle cx="3.46542" cy="1.56389" r="0.07984" transform="matrix(0.74366,-0.66856,0.66856,0.74366,-0.15721,2.71775)"/>
            <circle cx="3.04874" cy="1.41216" r="0.09627" transform="translate(0.52582 3.63114) rotate(-66.18581)"/>
            <circle cx="4.09885" cy="2.67803" r="0.04564" transform="translate(0.57348 6.07569) rotate(-77.10219)"/>
            <circle cx="4.15007" cy="2.26538" r="0.05703" transform="translate(-0.17901 0.3643) rotate(-4.92035)"/>
            <circle cx="4.07255" cy="1.85687" r="0.06842" transform="translate(-0.40118 1.71764) rotate(-22.72787)"/>
            <circle cx="3.87375" cy="1.49171" r="0.07981" transform="translate(-0.03927 2.87715) rotate(-40.55778)"/>
            <circle cx="3.57271" cy="1.20489" r="0.09122" transform="translate(0.67394 3.61594) rotate(-58.38851)"/>
            <circle cx="3.19835" cy="1.02395" r="0.10263" transform="translate(1.44091 3.88565) rotate(-76.19721)"/>
            <circle cx="2.7866" cy="0.96625" r="0.11932" transform="translate(-0.06094 0.19795) rotate(-4.02438)"/>
            <circle cx="4.49485" cy="2.81127" r="0.04564" transform="translate(0.52819 6.32873) rotate(-73.58876)"/>
            <circle cx="4.56679" cy="2.37976" r="0.057" transform="translate(2.01696 6.85381) rotate(-87.84461)"/>
            <circle cx="4.53008" cy="1.94389" r="0.06841" transform="translate(-0.30683 0.99277) rotate(-12.09987)"/>
            <circle cx="4.38702" cy="1.53052" r="0.07982" transform="translate(-0.22306 2.10987) rotate(-26.39541)"/>
            <circle cx="4.14642" cy="1.16521" r="0.09124" transform="translate(0.24201 2.98348) rotate(-40.66749)"/>
            <circle cx="3.82313" cy="0.87056" r="0.10264" transform="translate(0.9152 3.50079) rotate(-54.95738)"/>
            <circle cx="3.43717" cy="0.66472" r="0.11404" transform="translate(1.59648 3.64261) rotate(-69.2243)"/>
            <circle cx="3.01236" cy="0.56045" r="0.12547" transform="translate(2.115 3.49016) rotate(-83.5096)"/>
            <circle cx="2.57497" cy="0.56416" r="0.14752" transform="translate(-0.05272 0.35438) rotate(-7.79331)"/>
          </g>
          <g>
            <circle cx="2.66756" cy="0.57886" r="0.02281" transform="translate(1.45584 3.02481) rotate(-75.88963)"/>
            <circle cx="2.23352" cy="0.52451" r="0.03421" transform="translate(-0.00128 0.00546) rotate(-0.14015)"/>
            <circle cx="2.30196" cy="0.94372" r="0.03422" transform="translate(-0.00308 0.00755) rotate(-0.18782)"/>
            <circle cx="2.08891" cy="1.36777" r="0.0456" transform="translate(-0.00393 0.00601) rotate(-0.1648)"/>
            <circle cx="1.72557" cy="1.50786" r="0.05702" transform="translate(-0.46865 0.84707) rotate(-24.38832)"/>
            <circle cx="1.45174" cy="1.7847" r="0.06842" transform="translate(-0.84708 1.69464) rotate(-48.63019)"/>
            <circle cx="1.2575" cy="2.10265" r="0.07985" transform="translate(-1.12253 2.68393) rotate(-72.8431)"/>
            <circle cx="1.34117" cy="2.5381" r="0.09627" transform="translate(-0.30209 0.1843) rotate(-7.06734)"/>
            <circle cx="1.88845" cy="0.98713" r="0.04561" transform="translate(-0.21257 0.6316) rotate(-17.99247)"/>
            <circle cx="1.50806" cy="1.15499" r="0.05701" transform="translate(-0.39069 1.10042) rotate(-35.80144)"/>
            <circle cx="1.19728" cy="1.4312" r="0.06841" transform="matrix(0.59293,-0.80525,0.80525,0.59293,-0.6651,1.54671)"/>
            <circle cx="0.98595" cy="1.78928" r="0.07985" transform="translate(-1.02402 2.15483) rotate(-71.45158)"/>
            <circle cx="0.89432" cy="2.19485" r="0.09125" transform="translate(-1.31172 3.06119) rotate(-89.27159)"/>
            <circle cx="0.93123" cy="2.609" r="0.10265" transform="translate(-0.72539 0.38871) rotate(-17.08463)"/>
            <circle cx="1.09311" cy="2.99203" r="0.1193" transform="translate(-1.51574 1.16418) rotate(-34.91423)"/>
            <circle cx="1.7995" cy="0.57889" r="0.04561" transform="translate(-0.08748 0.46652) rotate(-14.42487)"/>
            <circle cx="1.39229" cy="0.73864" r="0.05701" transform="translate(-0.18365 0.76041) rotate(-28.7385)"/>
            <circle cx="1.03707" cy="0.9939" r="0.06843" transform="translate(-0.39923 0.97414) rotate(-42.99427)"/>
            <circle cx="0.75578" cy="1.32886" r="0.0798" transform="translate(-0.77077 1.24683) rotate(-57.29535)"/>
            <circle cx="0.56581" cy="1.72288" r="0.09123" transform="translate(-1.24759 1.71473) rotate(-71.56203)"/>
            <circle cx="0.47887" cy="2.15157" r="0.10264" transform="translate(-1.70173 2.47336) rotate(-85.84696)"/>
            <circle cx="0.50035" cy="2.58844" r="0.11404" transform="matrix(0.98442,-0.17581,0.17581,0.98442,-0.44727,0.12828)"/>
            <circle cx="0.62893" cy="3.00661" r="0.12544" transform="translate(-1.1861 0.52852) rotate(-24.40522)"/>
            <circle cx="0.85666" cy="3.38005" r="0.1475" transform="translate(-1.92434 1.27656) rotate(-38.67492)"/>
          </g>
        </g>
      </g>
    </svg>
  )
}

export function Spinner({
  size = 'md',
  className,
  label,
}: SpinnerProps) {
  const { t } = useTranslation()
  const loadingLabel = label || t('common.loading')
  return (
    <div
      role="status"
      aria-label={loadingLabel}
      className={cn('inline-flex', className)}
    >
      <LogoIcon
        className={cn(
          'animate-spin text-accent',
          sizeStyles[size]
        )}
        style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}
      />
      <span className="sr-only">{loadingLabel}</span>
    </div>
  )
}

/**
 * PageLoader - Full page loading indicator
 * Per SHARED-LOADING-STATES.md - shown after 300ms delay
 */
export interface PageLoaderProps {
  /** Text to display below spinner (uses translation if not provided) */
  message?: string
  className?: string
}

export function PageLoader({
  message,
  className,
}: PageLoaderProps) {
  const { t } = useTranslation()
  const loadingMessage = message || t('common.loading')

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center min-h-[400px]',
        className
      )}
      role="status"
      aria-busy="true"
    >
      <Spinner size="xl" />
      <p className="mt-4 text-body-sm text-gray-500">{loadingMessage}</p>
    </div>
  )
}

/**
 * InlineLoader - Small inline loading indicator
 */
export interface InlineLoaderProps {
  message?: string
  className?: string
}

export function InlineLoader({
  message,
  className,
}: InlineLoaderProps) {
  return (
    <span
      className={cn('inline-flex items-center gap-2', className)}
      role="status"
      aria-busy="true"
    >
      <Spinner size="sm" />
      {message && (
        <span className="text-body-sm text-gray-500">{message}</span>
      )}
    </span>
  )
}
